<template>
  <HrPageLayout
    title="Monitoring and Evaluation"
    subtitle="Skills audit M&E framework — maintain cycle records, capture agreed text for each row, and export for workshops or submission packs."
    icon=""
    :breadcrumb-items="breadcrumbItems"
  >
    <div v-if="!allowed" class="sam-access-denied">
      <i class="bx bx-lock-alt"></i>
      You do not have access to this module.
    </div>

    <!-- List -->
    <div v-else-if="pageMode === 'list'" class="sam-shell">
      <section class="sam-hero">
        <p class="sam-lead">
          Register each monitoring and evaluation cycle as a saved record. Open a past cycle to amend or export;
          start a new cycle when you begin a fresh skills audit M&amp;E action.
        </p>
      </section>

      <header class="sam-toolbar">
        <h2 class="sam-toolbar-title">M&amp;E records</h2>
        <div class="sam-toolbar-actions">
          <button type="button" class="sam-btn sam-btn--primary" @click="startNewSession">
            <i class="bx bx-plus-circle"></i>
            Create monitoring and evaluation
          </button>
          <button type="button" class="sam-btn sam-btn--outline" @click="loadSessions">
            <i class="bx bx-refresh"></i>
            Refresh
          </button>
        </div>
      </header>

      <div class="sam-table-card">
        <div v-if="loadingList" class="sam-loading"><i class="bx bx-loader-alt bx-spin"></i> Loading…</div>
        <div v-else class="sam-table-scroll">
          <table class="sam-table">
            <thead>
              <tr>
                <th>Plan cycle / label</th>
                <th>Last updated</th>
                <th>Created by</th>
                <th class="sam-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in sessions" :key="s.id">
                <td class="sam-strong">{{ s.cycle_label || '—' }}</td>
                <td>{{ formatDt(s.updated_at) }}</td>
                <td class="sam-muted">{{ s.created_by_username || '—' }}</td>
                <td class="sam-td-actions">
                  <button type="button" class="sam-btn sam-btn--sm sam-btn--ghost" @click="editSession(s.id)">Edit</button>
                  <button type="button" class="sam-btn sam-btn--sm sam-btn--outline" @click="exportSessionById(s.id)">Export</button>
                </td>
              </tr>
              <tr v-if="!sessions.length">
                <td colspan="4" class="sam-empty">No saved M&amp;E records yet. Use <strong>Create monitoring and evaluation</strong> to begin.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Form -->
    <div v-else class="sam-shell">
      <div class="sam-form-bar">
        <button type="button" class="sam-btn sam-btn--ghost" @click="backToList">
          <i class="bx bx-arrow-back"></i>
          Back to records
        </button>
        <div class="sam-form-bar-meta">
          <label class="sam-inline-label">Plan year / cycle label</label>
          <input v-model="cycleLabel" type="text" class="sam-input sam-input--inline" placeholder="e.g. FY 2026 / Cycle 1">
        </div>
      </div>

      <section class="sam-hero sam-hero--compact">
        <p class="sam-lead sam-lead--small">
          Framework reference text appears as <strong>hints</strong> inside each field — type your cycle-specific wording as needed.
          Comments capture narrative notes per row.
        </p>
      </section>

      <div class="sam-table-card sam-table-card--form">
        <div class="sam-table-scroll">
          <table class="sam-table sam-table--form">
            <thead>
              <tr>
                <th class="sam-col-sn">S/NO.</th>
                <th>Skill audit component</th>
                <th>Activities</th>
                <th>Responsibility</th>
                <th>Timelines</th>
                <th>Key performance indicators</th>
                <th>Measure of verification</th>
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in rows" :key="'me-' + idx">
                <td class="text-center sam-muted">{{ row.ref }}</td>
                <td class="sam-comp">{{ row.component }}</td>
                <td>
                  <textarea
                    v-model="row.activity"
                    class="sam-field"
                    rows="2"
                    :placeholder="placeholderAt(idx, 'activity')"
                  ></textarea>
                  <p v-if="row.hint" class="sam-hint">{{ row.hint }}</p>
                </td>
                <td>
                  <textarea
                    v-model="row.responsibility"
                    class="sam-field"
                    rows="2"
                    :placeholder="placeholderAt(idx, 'responsibility')"
                  ></textarea>
                </td>
                <td>
                  <textarea
                    v-model="row.timeline"
                    class="sam-field"
                    rows="2"
                    :placeholder="placeholderAt(idx, 'timeline')"
                  ></textarea>
                </td>
                <td>
                  <textarea
                    v-model="row.kpi"
                    class="sam-field"
                    rows="2"
                    :placeholder="placeholderAt(idx, 'kpi')"
                  ></textarea>
                </td>
                <td>
                  <textarea
                    v-model="row.verification"
                    class="sam-field"
                    rows="2"
                    :placeholder="placeholderAt(idx, 'verification')"
                  ></textarea>
                </td>
                <td>
                  <textarea v-model="row.comments" class="sam-field" rows="2" placeholder="Narrative comments for this row…"></textarea>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="sam-footer-card">
        <p class="sam-footer-hint">
          Save locks this cycle into the register. You can reopen it later from <strong>M&amp;E records</strong> or export anytime from the list.
        </p>
        <div class="sam-footer-btns">
          <button type="button" class="sam-btn sam-btn--outline sam-btn--lg" :disabled="saving" @click="exportCurrentCsv">
            <i class="bx bx-export"></i>
            Export CSV
          </button>
          <button type="button" class="sam-btn sam-btn--primary sam-btn--lg" :disabled="saving" @click="saveSession">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight } from "@/api";

const STORAGE_KEY_LEGACY = "skills_audit_me_comments_v1";

/** County M&E framework — used as placeholders + hint text only */
const ME_FRAMEWORK = [
  { ref: "01", component: "Planning", activity: "a) Define objectives and scope", responsibility: "M & E team, HR, Consultants", timeline: "Month 1", kpi: "Approved project plan", verification: "Plan document", hint: "Ensure alignment with strategic goals" },
  { ref: "01", component: "Planning", activity: "b) Devise methodology", responsibility: "", timeline: "", kpi: "", verification: "", hint: "" },
  { ref: "02", component: "Stakeholder(s) Engagement", activity: "a) Identify stakeholders", responsibility: "Project Lead, HR, Chief Officers", timeline: "Months 1–2", kpi: "No. of stakeholders", verification: "Meeting minutes, attendance records", hint: "Involve key offices, departments" },
  { ref: "02", component: "Stakeholder(s) Engagement", activity: "b) Conduct engagements / consultations", responsibility: "", timeline: "", kpi: "No. of consultations held", verification: "", hint: "" },
  { ref: "03", component: "Tool(s) Development", activity: "Develop key audit tools", responsibility: "HR, M&E teams, CHRMAC, CPSB", timeline: "Month 2–3", kpi: "No. of validated tools", verification: "Audit tools prototypes", hint: "Tools aligned with Public Service HR standards and policies" },
  { ref: "03", component: "Tool(s) Development", activity: "Validate the audit tools developed", responsibility: "", timeline: "", kpi: "", verification: "Validation minutes / report", hint: "" },
  { ref: "04", component: "Data collection", activity: "Conduct interviews, surveys, assessments", responsibility: "Audit field teams, HR", timeline: "Month 3–4", kpi: "% of employees assessed", verification: "Data collection logs, control sheets, reports", hint: "Ensure adherence to audit ethics e.g. confidentiality, data protection" },
  { ref: "05", component: "Data Analysis", activity: "Analyse collected data and generate insights", responsibility: "HR, Data analysts / ICT, M&E teams", timeline: "Month 4", kpi: "Completed analysis report", verification: "Analysis reports, dashboards", hint: "Highlights skills gaps and strengths" },
  { ref: "06", component: "Reporting", activity: "Prepare and share / formally submit audit report", responsibility: "M&E teams, HR", timeline: "Month 5–6", kpi: "No. of reports disseminated", verification: "Final audit report, distribution log", hint: "Use actionable recommendations" },
  { ref: "07", component: "Capacity Building", activity: "Design interventions to address skills gaps", responsibility: "HR, CPSB", timeline: "Post-audit (ongoing)", kpi: "No. of training programmes developed and executed", verification: "Training plans, attendance records", hint: "Based on audit findings" },
  { ref: "08", component: "Evaluation", activity: "Assess effectiveness of skills audit implementation", responsibility: "M&E teams", timeline: "Post-audit", kpi: "% of stakeholders satisfied", verification: "Feedback forms, evaluation reports", hint: "Use lessons learnt for improvements" },
  { ref: "09", component: "Continuous Monitoring and follow-up", activity: "Track progress of skills improvement initiatives", responsibility: "HR, M&E teams", timeline: "Ongoing", kpi: "% of targets achieved", verification: "Periodic M&E reports, performance reviews", hint: "Ensure alignment with goals" },
  { ref: "10", component: "Skills Audit Records and knowledge Management", activity: "Storage of audit documents / records for future reference and HR planning", responsibility: "HR, Records", timeline: "Post-audit", kpi: "No. of audit reports / documents", verification: "Database / storage accessibility and retrieval", hint: "Align with standard Public HR records management practices" }
];

function buildRowsFromFramework(mergeFrom = null) {
  return ME_FRAMEWORK.map((fr, i) => {
    const m = mergeFrom && Array.isArray(mergeFrom) && mergeFrom[i] ? mergeFrom[i] : {};
    return {
      ref: fr.ref,
      component: fr.component,
      activity: typeof m.activity === "string" ? m.activity : "",
      responsibility: typeof m.responsibility === "string" ? m.responsibility : "",
      timeline: typeof m.timeline === "string" ? m.timeline : "",
      kpi: typeof m.kpi === "string" ? m.kpi : "",
      verification: typeof m.verification === "string" ? m.verification : "",
      comments: typeof m.comments === "string" ? m.comments : "",
      hint: typeof m.hint === "string" && m.hint !== "" ? m.hint : fr.hint || ""
    };
  });
}

export default {
  name: "ModuleSkillsAuditMonitoringEvaluation",
  components: { HrPageLayout },
  data() {
    return {
      pageMode: "list",
      loadingList: false,
      saving: false,
      sessions: [],
      sessionId: null,
      cycleLabel: String(new Date().getFullYear()),
      rows: [],
      breadcrumbItems: [
        { label: "Home", route: "/dashboard" },
        { label: "County HRMIS (SR)" },
        { label: "Skills Audit" },
        { label: "Monitoring and Evaluation" }
      ]
    };
  },
  computed: {
    allowed() {
      return getRight("SKILLS AUDIT MONITORING EVALUATION");
    }
  },
  mounted() {
    if (this.allowed) this.loadSessions();
  },
  methods: {
    placeholderAt(idx, key) {
      const fr = ME_FRAMEWORK[idx];
      if (!fr) return "";
      const v = fr[key];
      return v != null && String(v).trim() !== "" ? String(v) : "";
    },

    formatDt(v) {
      if (v == null || v === "") return "—";
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    },

    async loadSessions() {
      this.loadingList = true;
      try {
        const fd = new FormData();
        fd.append("function", "listSkillsAuditMESessions");
        const res = await execute(fd, authUrl);
        const rec = res?.data?.success && Array.isArray(res?.data?.data?.records)
          ? res.data.data.records
          : [];
        this.sessions = rec;
      } catch (_) {
        this.sessions = [];
      } finally {
        this.loadingList = false;
      }
    },

    startNewSession() {
      this.sessionId = null;
      this.cycleLabel = String(new Date().getFullYear());
      this.rows = buildRowsFromFramework();
      this.applyLegacyLocalStorageComments();
      this.pageMode = "form";
    },

    /** One-time comfort: pull old browser-only comments into first new session if empty */
    applyLegacyLocalStorageComments() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY_LEGACY);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== "object") return;
        this.rows.forEach((r, i) => {
          const c = saved[`c${i}`];
          if (typeof c === "string" && c.trim() && !r.comments) r.comments = c;
        });
      } catch (_) {
        void 0;
      }
    },

    async editSession(id) {
      this.sessionId = id;
      try {
        const fd = new FormData();
        fd.append("function", "getSkillsAuditMESession");
        fd.append("id", String(id));
        const res = await execute(fd, authUrl);
        const s = res?.data?.success ? res?.data?.data?.session : null;
        if (!s) {
          alert(res?.data?.message || "Could not load record.");
          return;
        }
        this.cycleLabel = s.cycle_label || "";
        const merged = buildRowsFromFramework(Array.isArray(s.rows) ? s.rows : null);
        this.rows = merged;
        this.pageMode = "form";
      } catch (e) {
        alert(e?.message || "Load failed.");
      }
    },

    backToList() {
      this.pageMode = "list";
      this.loadSessions();
    },

    rowsPayload() {
      return this.rows.map((r) => ({
        ref: r.ref,
        component: r.component,
        activity: r.activity,
        responsibility: r.responsibility,
        timeline: r.timeline,
        kpi: r.kpi,
        verification: r.verification,
        comments: r.comments,
        hint: r.hint
      }));
    },

    async saveSession() {
      const label = String(this.cycleLabel || "").trim();
      if (!label) {
        alert("Enter a plan year / cycle label before saving.");
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "saveSkillsAuditMESession");
        if (this.sessionId) fd.append("id", String(this.sessionId));
        fd.append("cycle_label", label);
        fd.append("rows_json", JSON.stringify(this.rowsPayload()));
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          const newId = res?.data?.data?.id;
          if (newId) this.sessionId = newId;
          try {
            localStorage.removeItem(STORAGE_KEY_LEGACY);
          } catch (_) {
            void 0;
          }
          this.backToList();
        } else {
          alert(res?.data?.message || "Save failed.");
        }
      } catch (e) {
        alert(e?.message || "Save failed.");
      } finally {
        this.saving = false;
      }
    },

    exportCurrentCsv() {
      this.downloadCsv(this.rows, this.cycleLabel);
    },

    async exportSessionById(id) {
      try {
        const fd = new FormData();
        fd.append("function", "getSkillsAuditMESession");
        fd.append("id", String(id));
        const res = await execute(fd, authUrl);
        const s = res?.data?.success ? res?.data?.data?.session : null;
        if (!s || !Array.isArray(s.rows)) {
          alert("Could not load record for export.");
          return;
        }
        const rows = buildRowsFromFramework(s.rows);
        this.downloadCsv(rows, s.cycle_label || "export");
      } catch (_) {
        alert("Export failed.");
      }
    },

    downloadCsv(rowData, cycleLabel) {
      const headers = [
        "S/NO.",
        "Skill Audit Component",
        "Activities",
        "Responsibility",
        "Timelines",
        "Key Performance Indicators",
        "Measure of Verification",
        "Hint",
        "Comments",
        "Cycle label"
      ];
      const esc = (v) => {
        const s = String(v ?? "").replace(/"/g, '""');
        return `"${s}"`;
      };
      const lines = [headers.join(",")];
      rowData.forEach((r) => {
        lines.push([
          esc(r.ref),
          esc(r.component),
          esc(r.activity),
          esc(r.responsibility),
          esc(r.timeline),
          esc(r.kpi),
          esc(r.verification),
          esc(r.hint),
          esc(r.comments),
          esc(cycleLabel)
        ].join(","));
      });
      const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `skills_audit_me_${String(cycleLabel || "export").replace(/[^\w.-]+/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }
  }
};
</script>

<style scoped>
.sam-access-denied {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 22px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
}

.sam-shell {
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 40px;
}

.sam-hero {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 55%, #0f5980 100%);
  color: #fff;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 20px;
  box-shadow: 0 10px 32px rgba(13, 79, 115, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.sam-hero--compact {
  padding: 14px 18px;
  margin-bottom: 16px;
}

.sam-lead {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.95;
}

.sam-lead--small {
  font-size: 13px;
}

.sam-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.sam-toolbar-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.sam-toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sam-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}

.sam-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.sam-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(33, 149, 211, 0.35);
}

.sam-btn--outline {
  background: #fff;
  color: #0d4f73;
  border: 2px solid #94a3b8;
}

.sam-btn--ghost {
  background: transparent;
  color: #0d4f73;
  border: 1px solid #cbd5e1;
}

.sam-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
}

.sam-btn--sm {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 8px;
}

.sam-btn--lg {
  padding: 12px 22px;
  font-size: 15px;
}

.sam-table-card {
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 18px rgba(13, 79, 115, 0.07);
  overflow: hidden;
}

.sam-table-card--form {
  margin-bottom: 16px;
}

.sam-table-scroll {
  overflow-x: auto;
}

.sam-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.sam-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.sam-table th {
  text-align: left;
  padding: 12px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.sam-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  color: #334155;
}

.sam-table tbody tr:hover {
  background: #fafbfc;
}

.sam-table--form .sam-field {
  min-width: 140px;
}

.sam-col-sn {
  width: 52px;
}

.sam-comp {
  font-weight: 600;
  color: #0f172a;
  min-width: 120px;
}

.sam-field {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  resize: vertical;
  min-height: 44px;
  font-family: inherit;
}

.sam-field:focus {
  outline: none;
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.2);
}

.sam-field::placeholder {
  color: #94a3b8;
  font-size: 12px;
}

.sam-hint {
  margin: 6px 0 0;
  font-size: 11px;
  color: #64748b;
  font-style: italic;
}

.sam-strong {
  font-weight: 600;
  color: #0f172a;
}

.sam-muted {
  color: #64748b;
}

.sam-empty {
  text-align: center;
  color: #94a3b8;
  padding: 28px 16px !important;
}

.sam-loading {
  text-align: center;
  padding: 28px;
  color: #64748b;
}

.sam-th-actions,
.sam-td-actions {
  text-align: right;
  white-space: nowrap;
}

.sam-td-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.sam-form-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.sam-form-bar-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.sam-inline-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin: 0;
}

.sam-input {
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  min-width: 220px;
}

.sam-input--inline {
  max-width: 320px;
}

.sam-footer-card {
  border-radius: 14px;
  border: 1px solid #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
  box-shadow: 0 4px 20px rgba(13, 79, 115, 0.08);
  padding: 18px 22px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.sam-footer-hint {
  margin: 0;
  flex: 1;
  min-width: 200px;
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
}

.sam-footer-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
</style>
